// Stripe on the web (owners): connect the workspace's own Stripe account, and the invoices created from Tally with
// the state Stripe reports. The key goes to the server once and never comes back.
import { useEffect, useState } from 'react';
import type { InvoiceStatus, StripeInvoice, StripeStatus } from '../api/types.ts';
import { Banner, Field, SectionHeader, TextInput } from '../components/common.tsx';
import { useFmt, useStore } from '../store/hooks.tsx';
import { Button, StatusPill, type PillStateName } from '../ui/ds.tsx';

const PILLS: Record<InvoiceStatus, PillStateName> = { draft: 'idle', open: 'waiting', paid: 'done', void: 'paused', uncollectible: 'error' };

export function invoiceStatusLabel(t: (key: string) => string, status: InvoiceStatus): string {
  switch (status) {
    case 'draft': return t('Entwurf');
    case 'open': return t('Offen');
    case 'paid': return t('Bezahlt');
    case 'void': return t('Storniert');
    case 'uncollectible': return t('Uneinbringlich');
  }
}

export function InvoiceStatusPill({ status }: { status: InvoiceStatus }) {
  const t = useStore().t;
  return <StatusPill state={PILLS[status]} label={invoiceStatusLabel(t, status)} size="sm" />;
}

/** Settings › Invoices: the Stripe connection. */
export function StripeSettings() {
  const store = useStore();
  const t = store.t;
  const [status, setStatus] = useState<StripeStatus | null>(null);
  const [key, setKey] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { store.api.stripe().then(setStatus).catch((e: unknown) => setError(store.describe(e))); }, [store]);
  const run = async (action: () => Promise<StripeStatus>) => {
    setBusy(true);
    setError(null);
    try {
      setStatus(await action());
      setKey('');
    } catch (e) {
      setError(store.describe(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <SectionHeader title={t('Stripe')} />
      <section className="tl-card settings-card">
        {error ? <Banner tone="danger">{error}</Banner> : null}
        {store.api.guest ? <p className="note">{t('Stripe-Rechnungen gibt es nur mit Konto.')}</p> : !status ? <p className="note">{t('Lädt …')}</p> : !status.available ? (
          <p className="note">{t('Auf diesem Server ist Stripe nicht eingerichtet (SECRETS_KEY fehlt).')}</p>
        ) : status.connected ? (
          <>
            <Field label={t('Verbunden')}>
              <span className="settings-value">{[status.accountName, status.keyHint, status.livemode ? null : t('Testmodus')].filter(Boolean).join(' · ')}</span>
            </Field>
            <p className="note">{status.webhook
              ? t('Stripe meldet, wenn eine Rechnung abgeschlossen, bezahlt oder storniert wird.')
              : t('Der Schlüssel darf keine Webhooks anlegen: den Stand einer Rechnung holst du mit „Aktualisieren“.')}</p>
            <div className="settings-actions">
              <Button variant="plain" disabled={busy} onClick={() => void run(async () => { await store.api.disconnectStripe(); return store.api.stripe(); })}>{t('Trennen')}</Button>
            </div>
          </>
        ) : (
          <>
            <p className="note">{t('Tellary legt Rechnungen in deinem eigenen Stripe-Konto an: Kunde, Positionen und Umsatzsteuer. Nummer, Zahlungsseite und Versand übernimmt Stripe.')}</p>
            <Field label={t('Geheimer Schlüssel')} hint={t('Am sichersten ist ein eingeschränkter Schlüssel (rk_…) mit Schreibrechten für Kunden, Rechnungen, Rechnungsposten, Steuersätze und Webhook-Endpunkte. Der Schlüssel liegt verschlüsselt auf dem Server und wird nie wieder angezeigt.')}>
              <TextInput type="password" autoComplete="off" spellCheck={false} value={key} placeholder="rk_live_…" onChange={(e) => setKey(e.target.value)} />
            </Field>
            <div className="settings-actions">
              <Button variant="primary" icon="plug" disabled={busy || key.trim().length < 12} onClick={() => void run(() => store.api.connectStripe(key.trim()))}>{t('Verbinden')}</Button>
            </div>
          </>
        )}
      </section>
    </>
  );
}

/** Reports: the invoices created in Stripe, newest first. */
export function InvoiceList() {
  const store = useStore();
  const t = store.t;
  const fmt = useFmt();
  const [invoices, setInvoices] = useState<StripeInvoice[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { store.api.invoices().then(setInvoices).catch(() => setInvoices([])); }, [store, store.sheet]);
  if (!invoices || invoices.length === 0) return null;
  const refresh = async (id: string) => {
    setError(null);
    try {
      const updated = await store.api.refreshInvoice(id);
      setInvoices(invoices.map((i) => (i.id === id ? updated : i)));
    } catch (e) {
      setError(store.describe(e));
    }
  };
  return (
    <div className="stack">
      <SectionHeader title={t('Rechnungen in Stripe')} caption={String(invoices.length)} />
      {error ? <Banner tone="danger">{error}</Banner> : null}
      <div className="tl-card table">
        {invoices.map((invoice) => (
          <div key={invoice.id} className="table__row invoice-row">
            <div className="invoice-row__who">
              <strong>{invoice.number ?? t('Ohne Nummer')} · {invoice.clientName}</strong>
              <span className="note">{fmt.dayMonthYear(invoice.rangeStart)} – {fmt.dayMonthYear(invoice.rangeEnd - 1)}{invoice.livemode ? '' : ` · ${t('Testmodus')}`}</span>
            </div>
            <span className="table__num">{fmt.money(invoice.grossCents, invoice.currency, 2)}</span>
            <InvoiceStatusPill status={invoice.status} />
            <span className="invoice-row__actions">
              {invoice.dashboardUrl ? <a className="linklike invoice-row__link" href={invoice.dashboardUrl} target="_blank" rel="noreferrer">{t('In Stripe öffnen')}</a> : null}
              {invoice.status === 'draft' || invoice.status === 'open' ? <Button size="sm" variant="plain" onClick={() => void refresh(invoice.id)}>{t('Aktualisieren')}</Button> : null}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
